import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMeadowTheme } from '../../hooks/MeadowThemeContext';
import './NavBar.css';
import { scrollBehavior } from '../../utils/scrollBehavior';

const HOME_NAV_ITEMS = [
  { id: 'work', label: 'Work', to: '/#work' },
  /* Play hidden until it has live projects: { id: 'play', label: 'Play', to: '/#play' }, */
  { id: 'about', label: 'About', to: '/#about' },
  { id: 'resume', label: 'Resume', to: '/#resume' },
];

const HOME_SECTION_IDS = HOME_NAV_ITEMS.map((item) => item.id);
const MOBILE_NAV_MQ = '(max-width: 900px)';

const NavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dark, toggleTheme } = useMeadowTheme();
  const isCaseStudy = location.pathname.startsWith('/work/');
  /* Current case-study section, published by the page's SectionRail */
  const [section, setSection] = useState(null);
  useEffect(() => {
    const onSection = (event) => setSection(event.detail);
    window.addEventListener('cs:section', onSection);
    return () => window.removeEventListener('cs:section', onSection);
  }, []);
  const tabsRef = useRef(null);
  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: scrollBehavior() });
  };
  /* Slide the pill to the current tab and keep that tab in view */
  useLayoutEffect(() => {
    const box = tabsRef.current;
    if (!box) return undefined;
    const place = () => {
      const tab = box.querySelector('[aria-current="location"]');
      const item = tab?.parentElement;
      box.style.setProperty('--pill-on', item ? '1' : '0');
      if (!item) return;
      box.style.setProperty('--pill-x', `${item.offsetLeft}px`);
      box.style.setProperty('--pill-w', `${item.offsetWidth}px`);
      box.scrollTo({ left: item.offsetLeft - box.clientWidth / 2 + item.offsetWidth / 2, behavior: 'smooth' });
    };
    place();
    window.addEventListener('resize', place);
    document.fonts?.ready?.then(place);
    return () => window.removeEventListener('resize', place);
  }, [section?.active, isCaseStudy]);
  const isHome = location.pathname === '/';
  const navId = useId();
  const navRef = useRef(null);
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);
  const scrollLockY = useRef(0);
  const mobileMqRef = useRef(null);
  const menuOpenRef = useRef(false);

  const [scrolled, setScrolled] = useState(false);
  const [overMural, setOverMural] = useState(isHome);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  /* Permanent in-flow reserve on mobile while the bar is position:fixed */
  const [spacerPx, setSpacerPx] = useState(0);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((open) => !open);
  }, []);

  useEffect(() => {
    menuOpenRef.current = menuOpen;
  }, [menuOpen]);

  const measureSpacer = useCallback(() => {
    const nav = navRef.current;
    const mq = mobileMqRef.current;
    if (!nav || !mq?.matches) {
      setSpacerPx(0);
      return;
    }

    /* Never sample while the drawer is open — keep the last closed height. */
    if (menuOpenRef.current) return;

    const next = Math.ceil(nav.getBoundingClientRect().height);
    setSpacerPx((prev) => (prev === next ? prev : next));
  }, []);

  /* Track mobile breakpoint + keep a closed-height spacer in sync */
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mq = window.matchMedia(MOBILE_NAV_MQ);
    mobileMqRef.current = mq;

    const sync = () => {
      if (!mq.matches) {
        setMenuOpen(false);
        setSpacerPx(0);
        return;
      }
      measureSpacer();
    };

    sync();
    mq.addEventListener('change', sync);
    window.addEventListener('resize', measureSpacer, { passive: true });
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', measureSpacer);
    };
  }, [measureSpacer, location.pathname]);

  useEffect(() => {
    const updateNavState = () => {
      const nextScrolled = window.scrollY > 24;
      setScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled));

      if (isCaseStudy) {
        setOverMural(false);
        return;
      }

      if (location.pathname === '/') {
        const mural = document.querySelector('.home__hero');
        let nextOverMural;
        if (!mural) {
          nextOverMural = window.scrollY < 80;
        } else {
          nextOverMural = mural.getBoundingClientRect().bottom > 88;
        }
        setOverMural((prev) => (prev === nextOverMural ? prev : nextOverMural));
      } else {
        setOverMural(false);
      }
    };

    updateNavState();
    window.addEventListener('scroll', updateNavState, { passive: true });
    window.addEventListener('resize', updateNavState, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateNavState);
      window.removeEventListener('resize', updateNavState);
    };
  }, [location.pathname, isCaseStudy]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveId(null);
      return undefined;
    }

    const updateActive = () => {
      const current = HOME_SECTION_IDS.reduce((found, id) => {
        const el = document.getElementById(id);
        if (!el) return found;
        return el.getBoundingClientRect().top <= 140 ? id : found;
      }, null);
      setActiveId((prev) => (prev === current ? prev : current));
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
    };
  }, [location.pathname]);

  /* Route changes dismiss the drawer */
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  /*
    Body position:fixed scroll lock — preserves scrollY across open/close.
    overflow:hidden alone unsticks sticky and can drift scroll on unlock.
  */
  useEffect(() => {
    if (!menuOpen) return undefined;

    const html = document.documentElement;
    const body = document.body;
    scrollLockY.current = window.scrollY;
    const lockedPath = window.location.pathname;

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverflowAnchor: html.style.overflowAnchor,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
    };

    const scrollbarGap = Math.max(0, window.innerWidth - html.clientWidth);

    html.style.overflow = 'hidden';
    html.style.overflowAnchor = 'none';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollLockY.current}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    if (scrollbarGap > 0) {
      body.style.paddingRight = `${scrollbarGap}px`;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep Tab inside the open menu; the page behind it is locked
      const items = [...(navRef.current?.querySelectorAll('a[href], button') || [])].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onPointerDown = (event) => {
      const nav = navRef.current;
      if (nav && !nav.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    const focusTimer = window.setTimeout(() => {
      firstLinkRef.current?.focus({ preventScroll: true });
    }, 10);

    return () => {
      const y = scrollLockY.current;
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowAnchor = prev.htmlOverflowAnchor;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      body.style.paddingRight = prev.bodyPaddingRight;
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.clearTimeout(focusTimer);

      /* A link in the drawer navigated to another page: that page owns its
         scroll position (ScrollToHash / top), so don't drag the old y along. */
      if (window.location.pathname !== lockedPath) return;

      /*
        Restore instantly. html has `scroll-behavior: smooth`, so a plain
        scrollTo(0, y) after unpinning the body animated from the top back
        down to y — the page visibly scrolled "to the same spot" on close.
        Force instant for this jump only, then hand smooth scrolling back.
      */
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      html.style.scrollBehavior = prevBehavior;
    };
  }, [menuOpen, closeMenu]);

  const scrollToSection = (id) => (e) => {
    if (location.pathname !== '/') return;

    e.preventDefault();
    closeMenu();
    navigate(`/#${id}`, { replace: true });

    const el = document.getElementById(id);
    if (!el) return;

    /*
      Wait for scroll-lock cleanup (body unfixed + scrollY restore) before
      smooth-scrolling to the section, or the restore fights scrollIntoView.
    */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      });
    });
  };

  const navbarClassName = [
    'navbar',
    'navbar--meadow',
    isCaseStudy && 'navbar--case-study',
    isHome && 'navbar--home',
    overMural && 'navbar--over-mural',
    scrolled && 'navbar--scrolled',
    menuOpen && 'navbar--open',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {spacerPx > 0 ? (
        <div
          className="navbar__spacer"
          style={{ height: spacerPx }}
          aria-hidden="true"
        />
      ) : null}
      <header ref={navRef} className={navbarClassName} role="banner">
        <div className="navbar__shell">
          <div className="navbar__inner">
            {isCaseStudy ? (
              <Link
                to="/#work"
                className="navbar__back"
                aria-label="Back"
                onClick={closeMenu}
              >
                <span className="navbar__back-icon" aria-hidden="true">
                  ←
                </span>
                <span className="navbar__back-label">Back</span>
              </Link>
            ) : (
              <Link
                to="/"
                className="navbar__brand navbar__brand--meadow"
                onClick={() => {
                  closeMenu();
                  if (window.location.pathname !== '/' || window.location.hash) return;
                  // Already home: the route won't change, so scroll here, after any menu lock is released
                  requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: scrollBehavior() })));
                }}
                aria-label="Nivedha, home"
              >
                <span className="navbar__monogram" aria-hidden="true">
                  NIVEDHA
                </span>
              </Link>
            )}

            {isCaseStudy && section?.items?.length ? (
              <nav className="navbar__tabs" aria-label="Case study sections">
                <ol ref={tabsRef}>
                  {section.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        aria-current={item.id === section.active ? 'location' : undefined}
                        onClick={() => jumpTo(item.id)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ol>
                {/* Phones/tablets: the platform's own picker instead of a sideways scroll */}
                <label className="navbar__jump">
                  <span className="visually-hidden">Jump to section</span>
                  <select
                    aria-label="Jump to section"
                    value={section.active || ''}
                    onChange={(event) => event.target.value && jumpTo(event.target.value)}
                  >
                    {!section.active ? (
                      <option value="" disabled>
                        Sections
                      </option>
                    ) : null}
                    {section.items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <span className="navbar__jump-count" aria-hidden="true">
                    {section.active
                      ? `${section.items.findIndex((item) => item.id === section.active) + 1}/${section.items.length}`
                      : section.items.length}
                  </span>
                  <svg className="navbar__jump-caret" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </label>
              </nav>
            ) : null}

            {!isCaseStudy ? (
            <nav id={navId} aria-label="Main">
              <ul className="navbar__links">
                {HOME_NAV_ITEMS.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      ref={index === 0 ? firstLinkRef : undefined}
                      to={item.to}
                      className={`navbar__link${
                        isHome && activeId === item.id
                          ? ' navbar__link--active'
                          : ''
                      }`}
                      onClick={
                        isHome && item.to?.startsWith('/#')
                          ? scrollToSection(item.to.slice(2))
                          : closeMenu
                      }
                      aria-current={
                        isHome && activeId === item.id ? 'page' : undefined
                      }
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            ) : null}

            <div className="navbar__actions">
              <button
                type="button"
                className={`navbar__theme${dark ? ' navbar__theme--dark' : ''}`}
                onClick={toggleTheme}
                aria-pressed={dark}
                aria-label={
                  dark ? 'Switch to light mode' : 'Switch to dark mode'
                }
              >
                <span className="navbar__theme-track" aria-hidden="true">
                  <span className="navbar__theme-thumb">
                    {dark ? '☾' : '☼'}
                  </span>
                </span>
                <span className="navbar__theme-label">
                  {dark ? 'Dark' : 'Light'}
                </span>
              </button>

              {!isCaseStudy ? (
              <button
                ref={toggleRef}
                type="button"
                className="navbar__toggle"
                aria-expanded={menuOpen}
                aria-controls={navId}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                onClick={toggleMenu}
              >
                <span className="navbar__toggle-label" aria-hidden="true">
                  {menuOpen ? 'Close' : 'Menu'}
                </span>
                <span className="navbar__toggle-icon" aria-hidden="true">
                  <span />
                  <span />
                </span>
              </button>
              ) : null}
            </div>
          </div>
        </div>
      </header>

    </>
  );
};

export default NavBar;
